import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/GeneralComponents/Button";
import CustomUploadFileInput from "../../components/GeneralComponents/CustomUploadFileInput";
import ErrorsWrapper from "../../components/GeneralComponents/ErrorsWrapper";
import FormObserver from "../../components/GeneralComponents/FormObserver";
import GeneralInput from "../../components/GeneralComponents/GeneralInput";
import GeneralSelectInput from "../../components/GeneralComponents/GeneralSelectInput";
import RegisterRequestReview from "../../components/Popups/RegisterRequestReview";
import { useAuthContext } from "../../context/AuthContext";
import {
  login,
  setNewAccountRole,
  setRole,
} from "../../redux/slices/authSlice";
import { RootState } from "../../redux/store";
import { ChosenFacilityValues } from "../../types";
import { useCreateFacility } from "../../utils/api/AuthAPIs/useCreateFacility";
import { useUserRegister } from "../../utils/api/AuthAPIs/useUserRegister";
import { useInfiniteAreas } from "../../utils/api/General/useInfiniteAreas";
import { useInfiniteCities } from "../../utils/api/General/useInfiniteCities";
import {
  BASE_LOCAL_IMAGES_DIR,
  TablePaginationCount,
  allowedFilesExtensions,
} from "../../utils/constants/CommonConstants";
import {
  pharmacy_type_active_IMG,
  warehouse_type_active_IMG,
} from "../../utils/constants/Images";
import useChosenFacilitySchema from "../../utils/validations/auth/ChosenFacilitySchema";

interface ChosenFacilityProps {
  OriginType: string;
}

const ChosenFacility: React.FC<ChosenFacilityProps> = ({ OriginType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { changeShowLeaveAuthRoutePopupAuthState } = useAuthContext();
  const chosenFacilitySchema = useChosenFacilitySchema();
  const { role, newAccountRole, facilityTypeId, user, token, countryId } =
    useSelector((state: RootState) => state.auth);
  const { ProfileCurrentFacility } = useSelector(
    (state: RootState) => state.profile,
  );

  const { inView } = useInView();

  //?=========================States=============================
  const [showReviewPopup, setShowReviewPopup] = useState<boolean>(false);
  const [showLeavePrompt, setShowLeavePrompt] = useState<boolean>(false);
  const [currentFacilityRole, setCurrentFacilityRole] = useState<string | null>(
    null,
  );
  const [cityListState, setCityListState] = useState<any>([]);
  const [areaListState, setAreaListState] = useState<any>([]);
  const [searchCityTerm, setSearchCityTerm] = useState<string>("");
  const [searchAreaTerm, setSearchAreaTerm] = useState<string>("");
  const [
    isCreatingNewAccountFromPreviousAccount,
    setIsCreatingNewAccountFromPreviousAccount,
  ] = useState<boolean>(false);
  const [selectedCity, setSelectedCity] = useState<{
    label: string;
    value: number | null;
  } | null>(null);

  const [selectedArea, setSelectedArea] = useState<{
    label: string;
    value: number | null;
  } | null>(null);

  const [initialValues, setInitialValues] = useState<ChosenFacilityValues>({
    facility_name_en: "",
    facility_name_ar: "",
    validationDocument: null,
    CityId: "",
    AreaId: "",
  });

  //?===================Get City API================
  const {
    data: InfiniteCitiesData,
    fetchNextPage: InfiniteCitiesFetchNextPage,
    hasNextPage: InfiniteCitiesHasNextPage,
  } = useInfiniteCities(
    {
      Name: searchCityTerm,
      Count: TablePaginationCount,
      CountryId: countryId,
    },
    token ? token : user?.token,
    true,
  );

  useEffect(() => {
    if (inView && InfiniteCitiesHasNextPage) {
      InfiniteCitiesFetchNextPage();
    }
  }, [inView, InfiniteCitiesFetchNextPage, InfiniteCitiesHasNextPage]);

  //?===================Get Areas API================

  const {
    data: InfiniteAreasData,
    fetchNextPage: InfiniteAreasFetchNextPage,
    hasNextPage: InfiniteAreasHasNextPage,
  } = useInfiniteAreas(
    {
      Name: searchAreaTerm,
      Count: TablePaginationCount,
      CityId: selectedCity?.value || ProfileCurrentFacility?.City?.Id || 0,
    },
    token ? token : user?.token,
    !!(selectedCity?.value || ProfileCurrentFacility?.City?.Id),
  );

  useEffect(() => {
    if (inView && InfiniteAreasHasNextPage) {
      InfiniteAreasFetchNextPage();
    }
  }, [inView, InfiniteAreasFetchNextPage, InfiniteAreasHasNextPage]);

  //?==================Handle City Changes====================
  const handleCitySelectChange = (
    value: any,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined,
    ) => void,
  ) => {
    if (value.value === selectedCity?.value) {
    } else {
      setSelectedCity({
        label: value.label,
        value: value.value,
      });
      setFieldValue("AreaId", "");
      setSelectedArea(null);
    }
  };

  //?=========================Mapping Data=================
  useEffect(() => {
    let CITYLISTS = InfiniteCitiesData?.pages.flatMap((item) =>
      item?.Data.map((city) => ({
        label: city.Name || "",
        value: city.Id || 0,
        labelEn: city.NameEn || "",
        labelAr: city.NameAr || "",
      })),
    );
    let AREALISTS = InfiniteAreasData?.pages.flatMap((item) =>
      item?.Data.map((area) => ({
        label: area.Name || "",
        value: area.Id || 0,
        labelEn: area.NameEn || "",
        labelAr: area.NameAr || "",
      })),
    );

    setCityListState(CITYLISTS);
    setAreaListState(AREALISTS);
  }, [InfiniteCitiesData, InfiniteAreasData]);

  //?=======================Setting Values For Selects =================
  useEffect(() => {
    if (selectedCity?.value) {
      setSelectedCity({
        label: selectedCity?.label ?? "",
        value: selectedCity?.value ?? 0,
      });
    } else {
      setSelectedCity({
        label: t("city"),
        value: 0,
      });
    }
    if (selectedArea?.value) {
      setSelectedArea({
        label: selectedArea?.label ?? "",
        value: selectedArea?.value ?? 0,
      });
    } else {
      setSelectedArea({
        label: t("area"),
        value: 0,
      });
    }
  }, []);

  //?===========POST Register New User =================================
  const formData = new FormData();
  formData.append("FacilityName", initialValues.facility_name_en);
  formData.append("FacilityNameAr", initialValues.facility_name_ar);
  formData.append("FacilityTypeId", `${facilityTypeId}`);
  formData.append("AreaId", initialValues.AreaId);
  if (initialValues.validationDocument) {
    formData.append("File", initialValues.validationDocument);
  }
  const {
    status: UserRegisterStatus,
    error: UserRegisterError,
    mutate: UserRegisterMutate,
  } = useUserRegister();
  //?===========POST Register New Facility =================================
  const {
    error: CreateFacilityError,
    mutate: CreateFacilityMutate,
    status: CreateFacilityStatus,
  } = useCreateFacility();

  //?====================Submit Register Form ===================
  const handleSubmit = async () => {
    if (OriginType === "account") {
      CreateFacilityMutate(
        {
          payload: formData,
          token: token ? token : user?.token,
        },
        {
          onSuccess: (response) => {
            if (response.Status) {
              setIsCreatingNewAccountFromPreviousAccount(true);
              dispatch(setNewAccountRole(null));
              setShowReviewPopup(true);
            }
          },
        },
      );
    } else {
      UserRegisterMutate(
        {
          payload: formData,
          token: token ? token : user?.token,
        },
        {
          onSuccess: (response) => {
            if (response.Status) {
              const userData = response.Data;
              if (!userData) {
                return;
              }
              const FacilityType =
                userData?.CurrentFacility?.FacilityType?.Code;
              const currentFacility = userData.CurrentFacility || {};
              const loginPayload = {
                id: userData?.Id,
                phoneNumber: userData?.PhoneNumber,
                fullName: userData?.FullName,
                token: userData?.Token,
                isVerified: userData?.IsVerified,
                currentFacility: currentFacility,
              };
              dispatch(login(loginPayload));
              dispatch(setRole(FacilityType));
              setShowLeavePrompt(true);
              setShowReviewPopup(true);
            }
          },
        },
      );
    }
  };

  //?=====Facility Role Based On Register Or Create New Account From Existing Account
  useEffect(() => {
    if (newAccountRole) {
      setCurrentFacilityRole(newAccountRole);
    } else {
      setCurrentFacilityRole(role);
    }
  }, [role, newAccountRole]);

  //?===============Showing Leave Prompt ========
  useEffect(() => {
    changeShowLeaveAuthRoutePopupAuthState(showLeavePrompt);
  }, [showLeavePrompt]);
  return (
    <>
      <article className="relative  flex w-full flex-col gap-6 px-8 py-10 xl:w-[75%]">
        <div className="flex flex-col items-center justify-center gap-2">
          {currentFacilityRole === "pharmacy" ? (
            <img
              className="w-[3.75rem]"
              src={`${BASE_LOCAL_IMAGES_DIR}/${pharmacy_type_active_IMG}`}
              alt="facility"
              loading="lazy"
            />
          ) : (
            <img
              className="w-[3.75rem]"
              src={`${BASE_LOCAL_IMAGES_DIR}/${warehouse_type_active_IMG}`}
              alt="facility"
              loading="lazy"
            />
          )}
          <p className="text-center text-2xl font-bold text-headingTextColor">
            {t(`domeCare_${currentFacilityRole}`)}
          </p>
          <p className="align-center text-center text-base text-textColor">
            {t(`domeCare_${currentFacilityRole}_desc`)}
          </p>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={chosenFacilitySchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors }) => {
            return (
              <Form className="flex flex-col gap-6">
                <GeneralInput
                  name="facility_name_en"
                  type="text"
                  englishOnly={true}
                  placeholder={t(`${currentFacilityRole}_name_en`)}
                  required={true}
                  autoFocus={true}
                />
                <GeneralInput
                  name="facility_name_ar"
                  type="text"
                  arabicOnly={true}
                  placeholder={t(`${currentFacilityRole}_name_ar`)}
                  required={true}
                />
                <div className="flex w-full items-start justify-between gap-6">
                  <div className="flex w-full flex-col gap-2">
                    <GeneralSelectInput
                      name="CityId"
                      options={cityListState}
                      value={selectedCity}
                      defaultValue={{
                        label:
                          (ProfileCurrentFacility?.City?.Name &&
                            ProfileCurrentFacility?.City?.Name !== "") ??
                          t("city"),
                        value: ProfileCurrentFacility?.City?.Id ?? "",
                      }}
                      onInputChange={(SearchedCityName) => {
                        setSearchCityTerm(SearchedCityName);
                      }}
                      placeholder={t("city")}
                      wrapperClassName="w-full"
                      onChange={(value) => {
                        handleCitySelectChange(value, setFieldValue);
                      }}
                      fetchNextPage={InfiniteCitiesFetchNextPage}
                      hasNextPage={InfiniteCitiesHasNextPage}
                    />
                  </div>

                  <div className="flex w-full flex-col gap-2">
                    <GeneralSelectInput
                      name="AreaId"
                      options={areaListState}
                      placeholder={t("area")}
                      value={selectedArea}
                      defaultValue={{
                        label: t("area"),
                        value: "",
                      }}
                      onInputChange={(SearchedAreaName) => {
                        setSearchAreaTerm(SearchedAreaName);
                      }}
                      onChange={(value) => {
                        setSelectedArea({
                          label: value.label,
                          value: value.value,
                        });
                      }}
                      wrapperClassName="w-full"
                      fetchNextPage={InfiniteAreasFetchNextPage}
                      hasNextPage={InfiniteAreasHasNextPage}
                    />
                  </div>
                </div>
                <CustomUploadFileInput
                  setFieldValue={setFieldValue}
                  FieldName={"validationDocument"}
                  allowedFormats={allowedFilesExtensions}
                  placeholder={
                    currentFacilityRole === "pharmacy"
                      ? t("pharmacy_facility_license")
                      : t("facility_license")
                  }
                  allowedFormatsPlaceholder={t("facility_license_extensions")}
                />
                <Button
                  title={
                    UserRegisterStatus === "pending" ||
                    CreateFacilityStatus === "pending"
                      ? t("loading")
                      : t("Continue")
                  }
                  type="submit"
                  variant="primary"
                  extraClassName="shadow-cta"
                  disabled={
                    UserRegisterStatus === "pending" ||
                    CreateFacilityStatus === "pending"
                  }
                />
                <FormObserver
                  values={values}
                  setInitialValues={setInitialValues}
                />
              </Form>
            );
          }}
        </Formik>

        {/* //?======Error Boundries=========== */}
        {UserRegisterError ? (
          <ErrorsWrapper message={UserRegisterError?.message} />
        ) : null}
        {CreateFacilityError ? (
          <ErrorsWrapper message={CreateFacilityError?.message} />
        ) : null}
      </article>

      {/* //?====================Show Request Review=================== */}
      {showReviewPopup ? (
        <RegisterRequestReview
          show={showReviewPopup}
          setShow={setShowReviewPopup}
          isCreatingNewAccountFromPreviousAccount={
            isCreatingNewAccountFromPreviousAccount
          }
        />
      ) : null}
    </>
  );
};

export default ChosenFacility;
